import { AlertTriangle, X, CheckCircle } from 'lucide-react'

interface SubmitConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  totalQuestions: number
  answeredCount: number
  unansweredCount: number
  markedForReviewCount: number
  isSubmitting: boolean
}

export default function SubmitConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  totalQuestions,
  answeredCount,
  unansweredCount,
  markedForReviewCount,
  isSubmitting,
}: SubmitConfirmModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Submit Test</h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">
              Are you sure you want to submit the test? This action cannot be undone.
            </p>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900">Test Summary</h4>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Questions:</span>
                <span className="font-medium text-gray-900">{totalQuestions}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Answered:</span>
                <span className="font-medium text-green-600">{answeredCount}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unanswered:</span>
                <span className="font-medium text-red-600">{unansweredCount}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Marked for Review:</span>
                <span className="font-medium text-purple-600">{markedForReviewCount}</span>
              </div>
            </div>

            {unansweredCount > 0 && (
              <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">
                  You have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}.
                  Unanswered questions will not be scored.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue Test
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Submit Test
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
